import $ from 'jquery';
import BaseGateway from './BaseGateway';

/**
 * UPMCheckoutSessionGateway - Adaptive Pricing, Phase 1 (Universal Payment Method only).
 *
 * The UPM gateway already consolidates every payment method into a single WC gateway with a
 * single Payment Element, so it maps 1:1 onto a Stripe CheckoutSession's PaymentElement with
 * none of the cross-gateway mount arbitration Phase 2's CheckoutSessionGateway needs. This is a
 * much smaller sibling: it owns the one `checkout` instance (stripe.initCheckoutElementsSdk())
 * and the one shared payment element, and lets a plain CheckoutController drive the rest of the
 * lifecycle unchanged.
 *
 * Extends BaseGateway directly rather than UniversalPaymentGateway - the non-AP UPM path
 * (upm-checkout.js / UniversalPaymentGateway) stays completely untouched, and the handful of
 * UPM-specific bits that matter for rendering (payment-method-type tracking, wallet options)
 * are small enough to re-state here.
 *
 * SCOPE: element creation/mounting, cart-total sync and element validation. Payment confirmation
 * (checkout.confirm() via loadActions) and the server-side session-to-order linking
 * (wc_stripe_process_payment_result) are a follow-up - see the "Still outstanding" notes in
 * .claude/adaptive-pricing/DESIGN.md. confirmPayment()/confirmSetup() below throw until then.
 *
 * @since 4.1.0
 */
class UPMCheckoutSessionGateway extends BaseGateway {

    checkout = null;

    currencyElement = null;

    paymentMethodType = '';

    _mounted = false;

    _lastSyncedSignature = null;

    _savePaymentMethod = false;

    /**
     * @param {Object} props
     * @param {Object} props.settings - The stripe_upm_data payload (elementOptions, layout, terms, ...).
     * @param {Object} props.session  - The checkoutSession payload ({client_secret, session_id}).
     */
    constructor(props) {
        super(props);
        this.session = props.session ?? {};
        this.capabilities.push('place_order_button', 'checkout_error', 'checkout_session');
    }

    initialize() {
        super.initialize();
        // WooCommerce's own order-review AJAX cycle updates the CheckoutSession's line items
        // server-side (UPMCheckoutSessionController::maybe_sync_session_with_cart). Wrapping that
        // request's completion in runServerUpdate tells the browser's checkout object to re-fetch
        // the session and re-render the payment element with the new localized totals - in place,
        // no remount. See DESIGN.md "Keeping elements in sync after a server-side session update".
        this.actions.addAction('updated_checkout', this.onUpdatedCheckout.bind(this));
        // Fired by DOMEvents.onPlaceOrderSuccess once the server has linked the session to the
        // order and returned the #response= hash.
        this.actions.addAction('checkout_session_requires_confirmation', this.onRequiresConfirmation.bind(this));
    }

    /**
     * Under AP no gateway ever collects a client-side payment method id - checkout.confirm()
     * does everything. Returning _elementsSubmitted lets the second onCheckoutPlaceOrder return
     * true so WooCommerce's native submit runs instead of re-entering processCheckout().
     *
     * @returns {boolean}
     */
    hasPaymentMethodId() {
        return this._elementsSubmitted;
    }

    /**
     * @param {Object} data - #response= payload: return_url, billing_details, etc.
     * @returns {Promise<void>}
     */
    async onRequiresConfirmation(data) {
        if (!this.controller.isPaymentGatewaySelected()) {
            return;
        }
        try {
            await this.confirmCheckoutSession(data);
            window.location.href = data.return_url;
        } catch (error) {
            this._elementsSubmitted = false;
            this.controller.getForm().removeClass('processing').unblock?.();
            this.controller.submitError(error);
        }
    }

    onStripeSDKLoaded(stripe) {
        super.onStripeSDKLoaded(stripe);
        this.setup();
    }

    /**
     * Creates the shared checkout instance + payment element. Synchronous -
     * initCheckoutElementsSdk() doesn't return a Promise (unlike the earlier privately-gated
     * initCustomCheckout it replaced). Guarded so the re-fired stripe_sdk_loaded on every
     * updated_checkout is a no-op.
     *
     * @returns {void}
     */
    setup() {
        if (this.checkout || !this.session.client_secret) {
            return;
        }
        this.checkout = this.stripe.initCheckoutElementsSdk({
            clientSecret: this.session.client_secret,
            adaptivePricing: {allowed: true}
        });
        this.paymentElement = this.checkout.createPaymentElement(this.buildPaymentElementOptions());
        this.currencyElement = this.checkout.createCurrencySelectorElement();
        this.currencyElement.on('change', (...args) => {
            console.log('currency change');
        })
        this._savePaymentMethod = $(`[name="wc-${this.id}-new-payment-method"]`).is(':checked');
        // Session was created server-side with the current totals.
        this._lastSyncedSignature = this.getCartSignature();
    }

    /**
     * The checkout instance / payment element are created in setup() on SDK load, so there's
     * nothing to do here. Overridden to a no-op so BaseGateway's version doesn't build a
     * standalone stripe.elements() instance we'd never use.
     *
     * @returns {void}
     */
    onPaymentMethodAvailable() {
    }

    /**
     * @param {HTMLElement} container
     * @returns {Promise<void>}
     */
    async mountPaymentElement(container) {
        this._elementsSubmitted = false;
        this.paymentElement.removeAllListeners();
        this.paymentElement.on('change', this.onPaymentElementChange.bind(this));
        this.paymentElement.on('loaderror', this.onLoadError.bind(this));
        this.paymentElement.on('availablepaymentmethodschange', this.onAvailablePaymentMethodsChange.bind(this));
        this.paymentElement.mount(container);
        this._mounted = true;
        this.mountCurrencyElement();
    }

    /**
     * @returns {void}
     */
    mountCurrencyElement() {
        const container = this.getCurrencySelectorContainer();
        if (this.currencyElement && container && !container.querySelector('iframe')) {
            this.currencyElement.mount(container);
        }
    }

    /**
     * Creates the currency selector's container as the first/last child of the gateway's own
     * payment_box (above/below both the saved-methods list and the new-card form), not inside
     * .wc-payment-form - WooCommerce core's tokenization-form.js hides/shows that div based on the
     * saved-token selection, and the currency selector should stay visible either way. Cached by
     * id so a remount (e.g. after updated_checkout) reuses it.
     *
     * @returns {?HTMLElement}
     */
    getCurrencySelectorContainer() {
        let container = document.getElementById('wc-stripe-currency-selector-element');
        if (container) {
            return container;
        }

        const box = document.querySelector(`.payment_box.payment_method_${this.id}`);
        if (!box) {
            return null;
        }

        container = document.createElement('div');
        container.id = 'wc-stripe-currency-selector-element';

        if (this.settings?.currencySelectorPosition === 'below_payment_methods') {
            container.classList.add('wc-stripe-currency-selector-below');
            box.appendChild(container);
        } else {
            container.classList.add('wc-stripe-currency-selector-above');
            box.insertBefore(container, box.firstChild);
        }

        return container;
    }

    onPaymentElementChange(event) {
        if (event?.value?.type) {
            this.setPaymentMethodType(event.value.type);
        }
    }

    setPaymentMethodType(type) {
        this.paymentMethodType = type;
        $('#_stripe_payment_method_type').val(type);
    }

    onLoadError() {
    }

    /**
     * The Payment Element only filters currency-unsupported types out of its own "new payment
     * method" tabs - saved tokens aren't something it renders/knows about, so they need the same
     * treatment applied by hand. There's no equivalent event on the Currency Selector Element
     * itself (and no cross-element broadcast), so this is the only signal available - see
     * CheckoutSessionController::add_payment_method_type_attribute() for where
     * data-payment-method-type comes from on each <li>.
     *
     * @param {Object} event
     * @returns {void}
     */
    onAvailablePaymentMethodsChange(event) {
        const methods = event?.paymentMethods || {};
        const $list = $(`li.payment_method_${this.id} .woocommerce-SavedPaymentMethods`);
        if (!$list.length) {
            return;
        }

        let $selectedUnavailable = null;

        $list.find('.wc-stripe-saved-method[data-payment-method-type]').each((i, el) => {
            const $li = $(el);
            // The map only lists types currently offered for the active currency - absent means
            // unavailable, same as an explicit false. Keys are camelCase, unlike our own snake_case
            // type strings, hence toEventPaymentMethodKey().
            const type = this.toEventPaymentMethodKey($li.data('paymentMethodType'));
            const available = methods[type]?.available === true;
            $li.toggleClass('wc-stripe-saved-method--unavailable', !available);
            $li.find('.wc-stripe-saved-method__input').prop('disabled', !available);
            if (!available && $li.hasClass('selected')) {
                $selectedUnavailable = $li;
            }
        });

        if ($selectedUnavailable) {
            this.reselectSavedMethod($list);
        }
    }

    /**
     * 'sepa_debit' -> 'sepaDebit', matching availablepaymentmethodschange's key casing.
     *
     * @param {string} type
     * @returns {string}
     */
    toEventPaymentMethodKey(type) {
        return type.replace(/_([a-z])/g, (m, c) => c.toUpperCase());
    }

    /**
     * Falls back to the first still-available saved method, or "new payment method" if none
     * qualify. Triggers a real click so it runs through the normal selection/collapse flow.
     * hideSavedMethods() only hides, never shows - a real click always follows "See more" (which
     * shows everything first), but this one is programmatic, so the target needs an explicit
     * show() to undo stale display:none from an earlier collapse.
     *
     * @param {jQuery} $list
     * @returns {void}
     */
    reselectSavedMethod($list) {
        const $fallback = $list.find('.wc-stripe-saved-method:not(.wc-stripe-saved-method--unavailable)').first();
        const $target = $fallback.length ? $fallback : $list.find('.woocommerce-SavedPaymentMethods-new');
        if ($target.length) {
            $target.show();
            $target.trigger('click');
        }
    }

    /**
     * The session's mode is fixed server-side, so instead of BaseGateway's element mode swap,
     * an update_checkout round trip lets maybe_sync_session_with_cart() read the checkbox from
     * post_data and set payment_intent_data.setup_future_usage. Ignores the echoed change event
     * WooCommerce fires on the checkbox after every updated_checkout (which would otherwise loop).
     */
    setSavePaymentMethod(value) {
        if (value === this._savePaymentMethod) {
            return;
        }
        this._savePaymentMethod = value;
        $(document.body).trigger('update_checkout');
    }

    /**
     * Re-syncs the browser's checkout state after WooCommerce's order-review request has already
     * pushed the new totals into the session server-side. Skipped when nothing the session
     * carries (currency + total) actually changed.
     */
    async onUpdatedCheckout() {
        if (!this.checkout) {
            return;
        }
        const signature = this.getCartSignature();
        if (signature === this._lastSyncedSignature) {
            return;
        }
        this._lastSyncedSignature = signature;
        try {
            const {actions} = await this.checkout.loadActions();
            const result = await actions.runServerUpdate(() => Promise.resolve());
            if (result?.type === 'error') {
                console.error('Adaptive Pricing runServerUpdate error:', result.error);
            }
        } catch (error) {
            console.error('Adaptive Pricing runServerUpdate failed:', error);
        }
    }

    /**
     * Mirrors what update_session_for_cart() sends to Stripe.
     *
     * @returns {string}
     */
    getCartSignature() {
        const saveMethod = this._savePaymentMethod ? 1 : 0;
        return `${this.controller.getCurrency()}:${this.controller.getTotalCents()}:${saveMethod}`;
    }

    /**
     * Overrides BaseGateway.buildPaymentElementOptions() - the Payment Element created from a
     * CheckoutSession supports a narrower option set than the standalone stripe.elements() one
     * (fields, layout, paymentMethodOrder, readOnly, terms, wallets). defaultValues in
     * particular isn't supported, so it's stripped. Wallet auto-display mirrors
     * UniversalPaymentGateway.
     *
     * @param {boolean} isUpdate
     * @returns {Object}
     */
    buildPaymentElementOptions(isUpdate = false) {
        const {defaultValues, ...options} = super.buildPaymentElementOptions(isUpdate);
        if (!isUpdate) {
            options.wallets = {
                ...options.wallets,
                applePay: 'auto',
                googlePay: 'auto'
            };
        }
        this._paymentElementOptions = options;
        return options;
    }

    /**
     * BaseGateway validates via this.elements.submit(); under a CheckoutSession there's no
     * per-gateway elements group, so validate through the checkout instance's actions instead.
     *
     * @returns {Promise<void>}
     */
    async validatePaymentElement() {
        const {actions} = await this.checkout.loadActions();
        const response = await actions.validateElements();
        if (response.error) {
            throw response.error;
        }
        if (response.selectedPaymentMethod) {
            this.setPaymentMethodType(response.selectedPaymentMethod);
        }
        this._elementsSubmitted = true;
    }

    /**
     * @param {Object} data    - Next-action payload from the server response.
     * @param {Object} options
     * @returns {Promise<Object>}
     */
    async confirmPayment(data = {}, options = {}) {
        return this.confirmCheckoutSession(data);
    }

    /**
     * checkout.confirm() isn't intent-type specific, so setup shares the same path.
     *
     * @param {Object} data
     * @param {Object} options
     * @returns {Promise<Object>}
     */
    async confirmSetup(data = {}, options = {}) {
        return this.confirmCheckoutSession(data);
    }

    /**
     * Confirms the CheckoutSession via the checkout instance - the session already owns the
     * PaymentIntent and the payment method collected by the payment element, so there's no
     * client_secret to pass. redirect: 'always' hands off to the returnUrl (order-received);
     * the checkout.session.completed webhook finalises the order.
     *
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async confirmCheckoutSession(data = {}) {
        const {actions} = await this.checkout.loadActions();
        const billing = data.billing_details || {};

        // A field the Payment Element is set to collect itself ('auto', per
        // buildPaymentElementOptions()/the base class's DOM/context-based logic) can't also be
        // passed to confirm() - Stripe errors on double-collecting the same field. Only include a
        // field here when the element was explicitly told not to collect it ('never').
        // billingDetails is either a blanket 'auto'/'never' string (every field) or an object with
        // its own value per field - handle both shapes explicitly rather than assuming an object.
        const billingFields = this._paymentElementOptions?.fields?.billingDetails;
        const providedByData = (field) => {
            if (billingFields === 'never') {
                return true;
            }
            if (!billingFields || billingFields === 'auto') {
                return false;
            }
            return billingFields[field] === 'never';
        };

        const billingAddress = providedByData('address') && billing.address?.country ? {
            address: billing.address,
            ...(providedByData('name') && billing.name && {name: billing.name}),
        } : null;

        const result = await actions.confirm({
            redirect: 'if_required',
            ...(data.return_url && {returnUrl: data.return_url}),
            ...(data.payment_method && {paymentMethod: data.payment_method}),
            ...(providedByData('email') && billing.email && {email: billing.email}),
            // phone_number_collection only permits the session to accept a phone via confirm()'s
            // phoneNumber - the Payment Element never renders its own phone field in ui_mode:
            // 'custom', so this is never something the element could be "double collecting".
            ...(billing.phone && {phoneNumber: billing.phone}),
            // billingAddress can't be combined with paymentMethod - a saved method already has its
            // own billing address on file.
            ...(!data.payment_method && billingAddress && {billingAddress})
        });

        if (result.type === 'error') {
            throw result.error;
        }
        return result;
    }

}

export default UPMCheckoutSessionGateway;
export {UPMCheckoutSessionGateway};